import { AlertCircle, X, Clock, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useSupportMode } from '@/contexts/SupportModeContext';
import { format } from 'date-fns';

export function SupportModeBanner() {
  const navigate = useNavigate();
  const { 
    isInSupportMode, 
    supportSession, 
    endSupportSession, 
    timeRemaining,
    extendSession 
  } = useSupportMode();

  if (!isInSupportMode || !supportSession) {
    return null;
  }

  const shopName = supportSession.targetShop?.name || 'Unknown Shop';
  const memberRole = supportSession.targetMembership?.role;
  const isUserMode = supportSession.mode === 'USER';

  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
  };

  const isExpiringSoon = timeRemaining !== null && timeRemaining < 600; // < 10 minutes

  return (
    <div className="bg-amber-500/10 border-b border-amber-500/20 px-4 py-2">
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0" />
          <div className="flex items-center gap-2 flex-wrap text-sm">
            <span className="font-semibold text-amber-900">Support Mode:</span>
            <span className="text-amber-800">
              {isUserMode ? (
                <>
                  <Users className="inline h-3 w-3 mr-1" />
                  Viewing as {memberRole || 'User'} in {shopName}
                </>
              ) : (
                <>Viewing {shopName}</>
              )}
            </span>
            {supportSession.triggeredFromBugId && (
              <span className="text-amber-700 text-xs">
                (from Bug #{supportSession.triggeredFromBugId.slice(0, 8)})
              </span>
            )}
            {timeRemaining !== null && (
              <span className={`text-xs flex items-center gap-1 ${isExpiringSoon ? 'text-red-600 font-semibold' : 'text-amber-600'}`}>
                <Clock className="h-3 w-3" />
                {formatTime(timeRemaining)} left
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isExpiringSoon && (
            <Button
              variant="ghost"
              size="sm"
              onClick={extendSession}
              className="flex-shrink-0 hover:bg-amber-500/20 text-amber-900"
            >
              <Clock className="h-4 w-4 mr-1" />
              Extend
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={async () => {
              const returnPath = supportSession.returnPath || '/';
              await endSupportSession();
              navigate(returnPath);
            }}
            className="flex-shrink-0 hover:bg-amber-500/20 text-amber-900"
          >
            <X className="h-4 w-4 mr-1" />
            Exit
          </Button>
        </div>
      </div>
    </div>
  );
}
